import { useEffect, useState } from 'react'
import Translator from '../components/Translator'
import ExtensionPromo from '../components/ExtensionPromo'
import LearnWarmUpCard from '../components/learn/LearnWarmUpCard'
import {
  parseHandoff,
  HANDOFF_PARAM_KEYS,
  type HandoffSpeakerGender,
  type HandoffListener,
} from '@/lib/handoff'
import { PREFS_KEY, DRAFT_KEY, HANDOFF_AUTOTRANSLATE_KEY } from '../components/translator/constants'
import type { AppPrefs } from '../components/translator/types'
import { loadPrefs } from '../components/translator/utils'
import type { GenderId, LangCode } from '@/lib/baba-api'

function speakerGenderToAppGender(gender: HandoffSpeakerGender): GenderId {
  return gender === 'f' ? 'female' : 'male'
}

function listenerToAppSpeakingTo(listener: HandoffListener): GenderId {
  if (listener === 'm') return 'to_male'
  if (listener === 'f') return 'to_female'
  return 'to_mixed_group'
}

/**
 * Prefills the translator from a landing-page hand-off (`?text=&from=&to=&
 * gender=&listener=&src=`), mirroring the exact draft-text + prefs storage
 * Translator already reads on its own mount — no changes to Translator.tsx
 * needed. Runs once, synchronously, before Translator's first render so its
 * initial-state reads see the hand-off values, not stale storage.
 */
function applyHandoffOnce(): void {
  if (typeof window === 'undefined') return
  const parsed = parseHandoff(window.location.search)
  if (!parsed) return

  if (parsed.text) {
    try {
      localStorage.setItem(DRAFT_KEY, parsed.text)
      // Ask Translator to run the hand-off text through its own runTranslate on
      // mount, so the visitor lands on a finished translation rather than a
      // prefilled box. Goes through the real pipeline (quota, sign-in gates),
      // never a side-channel request. Consumed once by Translator.tsx.
      sessionStorage.setItem(HANDOFF_AUTOTRANSLATE_KEY, '1')
    } catch {
      // localStorage unavailable (private mode, quota) — Translator falls
      // back to its own empty-draft default.
    }
  }

  const prefsPatch: Partial<AppPrefs> = {}
  if (parsed.from && parsed.from !== 'auto') prefsPatch.sourceLang = parsed.from as LangCode
  if (parsed.to) prefsPatch.targetLang = parsed.to as LangCode
  if (parsed.gender) prefsPatch.gender = speakerGenderToAppGender(parsed.gender)
  if (parsed.listener) prefsPatch.speakingTo = listenerToAppSpeakingTo(parsed.listener)

  if (Object.keys(prefsPatch).length > 0) {
    try {
      const existing = loadPrefs()
      localStorage.setItem(PREFS_KEY, JSON.stringify({ ...existing, ...prefsPatch }))
    } catch {
      // Same fallback as above — Translator uses its own defaults.
    }
  }
}

export default function TranslatorPage() {
  // Lazy useState initializer runs once, during TranslatorPage's own render —
  // guaranteed to complete before Translator (the child) mounts and reads
  // localStorage in its own effects.
  useState(applyHandoffOnce)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    if (!HANDOFF_PARAM_KEYS.some((key) => url.searchParams.has(key))) return
    HANDOFF_PARAM_KEYS.forEach((key) => url.searchParams.delete(key))
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)
  }, [])

  return (
    <>
      <Translator />
      <LearnWarmUpCard />
      <ExtensionPromo />
    </>
  )
}
